import { usePreventiveScreenings } from '@/hooks/use-preventive-screenings';
import { useNavigate } from 'react-router-dom';
import { GlassCard } from '@/components/ui/glass-card';
import { cn } from '@/lib/utils';
import {
  Shield,
  Calendar,
  Heart,
  Clock,
  AlertTriangle,
  ArrowRight
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

const categoryIcons = {
  routine: Shield,
  'age-based': Calendar,
  'condition-based': Heart,
};

const statusConfig = {
  overdue: {
    label: 'Overdue',
    color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700',
    borderAccent: 'border-l-red-500',
  },
  'due-soon': {
    label: 'Due Soon',
    color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700',
    borderAccent: 'border-l-amber-500',
  },
  recommended: {
    label: 'Recommended',
    color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700',
    borderAccent: 'border-l-blue-500',
  },
};

const priorityOrder = {
  high: 0,
  medium: 1,
  low: 2,
};

export default function ScreeningReminders() {
  const {
    screenings,
    overdueCount,
    nextScreening,
    isLoading
  } = usePreventiveScreenings();
  const navigate = useNavigate();

  // Sort: overdue first, then by priority
  const sortedScreenings = [...screenings].sort((a, b) => {
    if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1;
    return priorityOrder[a.priority as keyof typeof priorityOrder] -
           priorityOrder[b.priority as keyof typeof priorityOrder];
  });

  const handleSchedule = (path: string) => {
    navigate(path);
  };

  if (isLoading) {
    return (
      <GlassCard className="w-full">
        <div className="space-y-4 p-6">
          <div className="flex items-center justify-between">
            <div className="h-6 w-44 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            <div className="h-6 w-6 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
          </div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-28 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
          ))}
        </div>
      </GlassCard>
    );
  }

  return (
    <GlassCard className="w-full">
      <div className="space-y-4 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            Preventive Care
          </h2>
          {overdueCount > 0 && (
            <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-bold">
              <AlertTriangle className="h-3 w-3" />
              {overdueCount} Overdue
            </div>
          )}
        </div>

        {/* Screening List */}
        {screenings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
              <Shield className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
              All up to date!
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              No preventive screenings needed at this time
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedScreenings.map((screening) => {
              const Icon = categoryIcons[screening.basedOn as keyof typeof categoryIcons] || Shield;
              const status = screening.isOverdue
                ? 'overdue'
                : new Date(screening.recommendedDate).getTime() - Date.now() < 30 * 24 * 60 * 60 * 1000
                  ? 'due-soon'
                  : 'recommended';
              const statusInfo = statusConfig[status];

              return (
                <div
                  key={screening.id}
                  className={cn(
                    "rounded-lg border-l-4 border p-4 space-y-3 transition-all hover:shadow-md",
                    statusInfo.borderAccent,
                    screening.isOverdue
                      ? "bg-red-50/50 dark:bg-red-900/10 border-red-200 dark:border-red-800"
                      : "bg-white/50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700"
                  )}
                >
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 flex items-center justify-center">
                        <Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-semibold text-sm text-gray-900 dark:text-white">
                            {screening.screeningName}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                            {screening.description}
                          </p>
                        </div>
                        <div
                          className={cn(
                            "flex-shrink-0 px-2.5 py-1 rounded-full border text-xs font-medium",
                            statusInfo.color
                          )}
                        >
                          {statusInfo.label}
                        </div>
                      </div>

                      {/* Date Info */}
                      <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          <span>
                            {format(new Date(screening.recommendedDate), 'MMM d, yyyy')}
                          </span>
                        </div>
                        {screening.isOverdue && screening.overdueDays && (
                          <div className="flex items-center gap-1 text-red-600 dark:text-red-400 font-medium">
                            <Clock className="h-3 w-3" />
                            <span>{screening.overdueDays} days overdue</span>
                          </div>
                        )}
                        <span className="text-gray-500 dark:text-gray-500">
                          • Based on {screening.basedOn.replace('-', ' ')}
                        </span>
                      </div>

                      {/* Action Button */}
                      <button
                        onClick={() => handleSchedule(screening.actionPath)}
                        className={cn(
                          "flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                          screening.isOverdue
                            ? "bg-red-600 text-white hover:bg-red-700"
                            : "bg-blue-600 text-white hover:bg-blue-700"
                        )}
                      >
                        {screening.actionLabel || 'Schedule'}
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Next Screening Info */}
        {nextScreening && !nextScreening.isOverdue && (
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <Clock className="h-4 w-4" />
              <span>
                Next screening: <span className="font-medium text-gray-900 dark:text-white">
                  {nextScreening.screeningName}
                </span> in{' '}
                {formatDistanceToNow(new Date(nextScreening.recommendedDate), { addSuffix: false })}
              </span>
            </div>
          </div>
        )}
      </div>
    </GlassCard>
  );
}
